import {
  Box,
  Checkbox,
  Divider,
  HStack,
  Text,
  Textarea,
  useBoolean,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import { isAfter } from 'date-fns';
import React from 'react';
import { useOutletContext } from 'react-router-dom';

import { AppError } from '../../../../framework/models/errors';
import { env } from '../../../../framework/utils/EnvUtils';
import { TypeUtils } from '../../../../framework/utils/UtilityTypes';
import AttachedFiles from '../../../components/AttachedFiles';
import { AutoResizeTextarea } from '../../../components/AutoResizeTextarea';
import { useCheckArea } from '../../../components/Check';
import DateMatter from '../../../components/DateMatter';
import StyledButton, {
  BackButton,
  CommitButton,
} from '../../../components/StyledButton';
import { useGlobalStateMachine } from '../../../GlobalStore';
import { useEngeneNavigate } from '../../../hooks/navigate';
import { usePostWorkerAssignAccept } from '../../../hooks/queries/workerAssign';
import useToastHook from '../../../hooks/ToastHook';
import { EngeneFileObject } from '../../../models/EngeneFileObject';
import { FileKind } from '../../../models/gen/fileKind';
import NavigateGoBack from '../../../routes/GoBack';
import { Formatter } from '../../../utils/FormatUtils';
import { useMasterContext } from '../../MasterProvider';
import { ImageModal } from '../../modals/Image';
import { useSizeContext } from '../../SizeProvider';
import { AssignmentContext } from './Assignment';

const ROLE_TYPE_OUTSOURCER_USER = 1; // 外注(個人)/外注(法人)の role_type

/**
 * 手配
 * @param props -
 * @returns
 */
const AssignmentOperation: React.FunctionComponent = (props) => {
  const { actions, state } = useGlobalStateMachine();

  const { assignMessage, isLoading, isFetched } =
    useOutletContext<AssignmentContext>();
  if (!isLoading && isFetched && TypeUtils.isNil(assignMessage?.result)) {
    return <NavigateGoBack />;
  }

  const master = useMasterContext();
  const [selectedFile, setSelectedFile] = React.useState<EngeneFileObject>();
  const thisAssignedWorkers = assignMessage?.result?.assignedWorkers?.filter(
    (assignedWorker) => {
      if (state.userInfo?.role_type === ROLE_TYPE_OUTSOURCER_USER) {
        // ユーザが外注(個人)/外注(法人)の場合、全件が対象
        return true;
      } else {
        // ユーザSE(非外注)の場合、当該ユーザのみ対象
        const targetMatterWorkerId = assignMessage?.reference?.find(
          (w) => w.userCode === state.userInfo?.user_code
        )?.matterWorkerId;
        return (
          assignedWorker.assignedWorkerSummary?.matterWorkerId ===
          targetMatterWorkerId
        );
      }
    }
  );

  let latestAnswerTime: Date | undefined = undefined;
  {
    thisAssignedWorkers
      ?.map((assignedWorker) => assignedWorker.assignedWorkerAnswer?.answerTime)
      .filter((dateString) => TypeUtils.isNonNilString(dateString))
      .forEach((dateString) => {
        const d = Formatter.createDate(dateString!);
        if (TypeUtils.isNil(latestAnswerTime)) {
          latestAnswerTime = d;
        } else if (isAfter(d, latestAnswerTime)) {
          latestAnswerTime = d;
        }
      });
  }

  const isAssigned =
    thisAssignedWorkers?.every(
      (assignedWorker) =>
        assignedWorker.assignedWorkerAnswer?.assignStatus === 2
    ) && !TypeUtils.isNil(latestAnswerTime);
  // if (props.mode === 'edit' && isAssigned) {
  //   // 了承済手配を編集モードで開いた場合は閲覧モードへリダイレクト
  //   return (
  //     <Navigate
  //       replace
  //       to={`${pagePaths.ASSIGNMENT_SE}/${params.assignMessageId}/${pageKeywords.mode.VIEW}`}
  //     />
  //   );
  // }

  const sizes = useSizeContext();
  const navigate = useEngeneNavigate();
  const {
    isOpen: isOpenImage,
    onClose: onCloseImage,
    onOpen: onOpenImage,
  } = useDisclosure();

  // const { ImageModal, onOpen: onOpenImage } = useImageModal();
  const { CheckArea, checked } = useCheckArea();
  const [checkedAgreement, { toggle: toggleAgreement }] = useBoolean(
    !state.userInfo?.roleOptions?.isOutsourcer
  );

  const { toast } = useToastHook();
  const postWorkerAssignAccept = usePostWorkerAssignAccept();

  return (
    <VStack
      alignItems="start"
      fontSize={sizes.fontSize.rawMessage}
      width={sizes.width.styledMessageList}
    >
      <DateMatter
        customerName={assignMessage?.result?.customerName}
        dateString={assignMessage?.result?.date}
        matterId={
          Array.isArray(assignMessage?.reference) &&
          assignMessage!.reference!.length > 0
            ? assignMessage?.reference[0].matterId
            : undefined
        }
        matterName={assignMessage?.result?.matterName}
      />
      <Divider />
      <Text
        hidden={TypeUtils.isNilString(assignMessage?.result?.sendTime)}
        textAlign="left"
      >
        {Formatter.dateTimeOfWeekString(assignMessage?.result?.sendTime)} 送信
      </Text>
      <Text hidden={!isAssigned} textAlign="left">
        {isAssigned ? Formatter.dateTimeOfWeek(latestAnswerTime!) : '―'} 了承済
      </Text>
      <Text
        fontSize={sizes.fontSize.rawHeader}
        fontWeight="bold"
        textAlign="left"
      >
        {assignMessage?.result?.subject}
      </Text>
      <Box
        backgroundColor="white"
        borderColor="gray.300"
        borderRadius="md"
        borderWidth={1}
        height={300}
        padding={2}
        textAlign="left"
        width="100%"
        // as={AutoResizeTextarea}
        as={Textarea}
      >
        {assignMessage?.result?.message}
      </Box>
      <AttachedFiles
        fileKind={FileKind.Message}
        files={assignMessage?.result?.attachFiles}
      />
      <CheckArea
        additionalArea={
          <>
            <VStack
              hidden={!state.userInfo?.roleOptions?.isOutsourcer}
              flexWrap="wrap"
            >
              {/* <Text>受注に際し、</Text>
              <StyledButton
                fontSize={sizes.fontSize.listMessage}
                onClick={() => {
                  const a = document.createElement('a');
                  a.href = `${env.APP_ROOT}/pdf/外注契約約款.pdf`;
                  a.download = '外注契約約款.pdf';
                  a.click();
                }}
                variant="link"
              >
                外注契約約款
              </StyledButton>
              <Text>の確認が必要です。</Text> */}
              <Text>
                受注に際し、
                <span
                  onClick={() => {
                    const a = document.createElement('a');
                    a.href = `${env.APP_ROOT}/pdf/外注契約約款.pdf`;
                    a.download = '外注契約約款.pdf';
                    a.click();
                  }}
                  style={{
                    color: 'var(--chakra-colors-blue-500)',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    textDecoration: 'underline',
                  }}
                >
                  外注契約約款
                </span>
                の確認が必要です。
              </Text>
            </VStack>
            <Checkbox
              hidden={!state.userInfo?.roleOptions?.isOutsourcer}
              isChecked={checkedAgreement}
              onChange={toggleAgreement}
            >
              <Text fontSize={sizes.fontSize.confirmMessage}>確認しました</Text>
            </Checkbox>

            <Divider hidden={!state.userInfo?.roleOptions?.isOutsourcer} />
          </>
        }
        attension={
          <>
            <Text color="red" fontWeight="bold" textAlign="left">
              ※ 手配内容に問題がある場合は、別途ご担当者様に確認してください
            </Text>
            <Text
              color="red"
              fontWeight="bold"
              hidden={
                TypeUtils.isNonNilNumber(master.role.representative?.id) &&
                !state.userInfo?.role_id.includes(
                  master.role.representative!.id!
                )
              }
              textAlign="left"
            >
              ※ 受注了承後、職人アサインをおこなってください。
            </Text>
          </>
        }
        button={CommitButton}
        buttonProps={{ isDisabled: !checked || !checkedAgreement }}
        confirmMessage="上記内容を確認しました"
        hidden={isAssigned}
        onClick={() => {
          actions.setGuard(true);
          assignMessage?.result?.assignedWorkers
            ?.filter((assignedWorker) => {
              if (state.userInfo?.role_type === ROLE_TYPE_OUTSOURCER_USER) {
                // ユーザが外注(個人)/外注(法人)の場合、全件が対象
                return true;
              } else {
                // ユーザSE(非外注)の場合、当該ユーザのみ対象
                const targetMatterWorkerId = assignMessage?.reference?.find(
                  (w) => w.userCode === state.userInfo?.user_code
                )?.matterWorkerId;
                return (
                  assignedWorker.assignedWorkerSummary?.matterWorkerId ===
                  targetMatterWorkerId
                );
              }
            })
            .forEach((assignedWorker) =>
              postWorkerAssignAccept.mutate(
                {
                  assignWorkerId:
                    assignedWorker.assignedWorkerSummary!.assignWorkerId!,
                },
                {
                  onSuccess: () => {
                    navigate();
                    actions.setGuard(false);
                  },
                  onError: (error) => {
                    toast(
                      error instanceof AppError ? error : '了承に失敗しました'
                    );
                    actions.setGuard(false);
                  },
                }
              )
            );
        }}
      />
      <BackButton onClick={() => navigate(-1)} />
      <ImageModal
        file={selectedFile}
        isOpen={isOpenImage}
        onClose={() => {
          setSelectedFile(undefined);
          onCloseImage();
        }}
      />
    </VStack>
  );
};

export default AssignmentOperation;
